<script lang="ts" setup>
import { type Ref } from "vue";
import { STATES } from "~/constants/states";
import type Product from "~~~/types/Product";
import { useHelper } from "~/composables/helper";

// Properties
const props = defineProps({
    state: {
        type: String,
        default: "ga",
    },
    product: {
        type: Object,
    },
});

// Data
const books: Ref<Product[]> = ref([]);
const swiperInitialized = ref(false);
const loading = ref(false);

// Functions
const initializeSwiper = () => {
    // swiper element
    const swiperEl = document.querySelector("#additional-book-slides");
    const btnNext = document.querySelector("#additional-book-slides-btn-next");
    const btnPrev = document.querySelector("#additional-book-slides-btn-prev");

    // swiper parameters
    const swiperParams = {
        slidesPerView: 2,
        spaceBetween: 15,
        loop: false,
        navigation: {
            nextEl: btnNext,
            prevEl: btnPrev,
        },
        autoplay: {
            delay: 8000,
            disableOnInteraction: true,
            pauseOnMouseEnter: true,
        },
        breakpoints: {
            640: { slidesPerView: 2 },
            768: { slidesPerView: 3 },
            1024: { slidesPerView: 4 },
            1280: { slidesPerView: 6 },
        },
        on: {
            init() {
                // ...
            },
        },
    };

    if (swiperEl && books.value.length > 0) {
        // now we need to assign all parameters to Swiper element
        Object.assign(swiperEl, swiperParams);

        // and now initialize it
        swiperEl?.initialize();
    }

    swiperInitialized.value = true;
};

// Get products with same state
async function fetchProducts() {
    try {
        if (props.state === "") {
            return;
        }

        loading.value = true;
        const filtersAndFields = [`fields=${useHelper().collectionProductFields}`];
        filtersAndFields.push(
            `filter[states:contains]=${props.state.toLowerCase()}&filter[id:not]=${props.product?.c_id}`,
        );
        books.value = (await useHelper().getCollection<Product>("products", filtersAndFields.join("&"), 20)) ?? [];

        initializeSwiper();
    } catch (e) {
        console.log(e);
    } finally {
        loading.value = false;
    }
}

await fetchProducts();

// Watch property state changes. Fetch new books.
watch(
    () => props.state,
    async (newVal, oldVal) => {
        fetchProducts();
    },
);
</script>

<template>
    <div>
        <div
            v-show="loading"
            class="text-center"
        >
            <img
                src="/images/spinnersBlocksWave.svg"
                alt="Spinner"
                class="mx-auto w-20"
            />
            Loading...
        </div>
        <div v-show="swiperInitialized">
            <HrWithText class="mb-8">
                <h2 class="text-xl font-semibold lg:text-2xl">
                    Looking for additional {{ STATES[state.toUpperCase()] }} books?
                </h2>
            </HrWithText>

            <div class="relative pb-6">
                <swiper-container
                    id="additional-book-slides"
                    init="false"
                    class="swiper-container swiper-initialized swiper-horizontal px-2 md:w-[70%] lg:w-[80%] xl:w-[87%]"
                >
                    <swiper-slide
                        v-for="(product, index) in books"
                        :key="index"
                        class="overflow-hidden"
                    >
                        <NuxtLink :to="`/products/${product.c_slug}`">
                            <img
                                :src="product.photo_lg"
                                :srcset="`${product.photo_lg} 1x, ${product.photo_lg_2x} 2x`"
                                width="205"
                                height="265"
                                :alt="'Cover Image ' + product.title"
                                class="mx-auto mb-3 w-full border border-gray-300 shadow-md shadow-gray-400"
                            />
                            <div>{{ product.title }}</div>
                        </NuxtLink>
                    </swiper-slide>
                </swiper-container>

                <div
                    class="absolute top-0 right-0 z-10 hidden h-full items-center justify-center border-l-0 bg-white p-4 shadow-xl shadow-white md:flex"
                >
                    <div
                        id="additional-book-slides-btn-next"
                        class="flex-none cursor-pointer rounded-full border px-5 py-3 shadow-lg hover:border-2"
                    >
                        <img
                            src="/images/fa/chevron-right-regular.svg"
                            class="w-5"
                        />
                    </div>
                </div>
                <div
                    class="absolute top-0 left-0 z-10 hidden h-full items-center justify-center border-r-0 bg-white p-4 shadow-xl shadow-white md:flex"
                >
                    <div
                        id="additional-book-slides-btn-prev"
                        class="flex-none cursor-pointer rounded-full border px-5 py-3 shadow-lg hover:border-2"
                    >
                        <img
                            src="/images/fa/chevron-left-regular.svg"
                            class="w-5"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped></style>
